import { useEffect, useState } from 'react';
import { useQuery, type QueryKey } from '@tanstack/react-query';
import { useGeoRepository, usePhoneBookPort } from '../container';
import { keys } from '../query-keys';

/**
 * Geography and telephone numbers for a doctor's form: the countries, a
 * country's regions, the cities and areas suggested while one is typed, and
 * the telephone library. The request of 6 October 2026 (fifth), section 2.
 *
 * Two of these are not calls at all. The countries are a file the app carries
 * and the telephone rules are a library; each is loaded the first time a
 * screen asks, as a chunk of its own, and kept for the life of the page.
 */

/** How long typing has to pause before a place is searched for: the pause every search box takes. */
export const PLACE_SEARCH_DEBOUNCE_MS = 200;

/** How many places a field suggests: a short list under a field, not a page. */
export const PLACE_SEARCH_LIMIT = 20;

/** Regions change with a new edition of the list, not while a form is open. */
const REGIONS_STALE_MS = 60 * 60 * 1000;

/** Suggestions include what doctors' addresses say, so they age as a list does. */
const PLACES_STALE_MS = 15_000;

/**
 * All 250 countries, by name, each with its address format: which parts the
 * country's post asks for, what its postal code is called and looks like, and
 * how the lines of a label run. `data` is undefined for the moment the file
 * takes to arrive, and then the same array for the life of the page. What a
 * country selector lists, what `addressProblems` and `addressLines` are
 * handed, and where a telephone selector finds the name of a code.
 */
export function useCountries() {
  const repo = useGeoRepository();
  return useQuery({
    queryKey: keys.geo.countries,
    queryFn: () => repo.countries(),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/**
 * A country's regions, by name, for a region field the person chooses from or
 * types in. Asks for nothing until there is a country. A country the list has
 * no regions for answers an empty list, and the field is then typed.
 */
export function useRegions(country: string | null | undefined) {
  const repo = useGeoRepository();
  const code = (country ?? '').trim().toUpperCase();
  return useQuery({
    queryKey: keys.geo.regions(code),
    queryFn: () => repo.regions(code),
    enabled: code !== '',
    staleTime: REGIONS_STALE_MS,
  });
}

/**
 * A value that follows another a moment later, once it has stopped changing.
 * Clearing is not delayed. The shared hook of the same name belongs to the
 * screens, which this layer may not import.
 */
function useSettled(value: string, delayMs: number): string {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    if (value === '') {
      setSettled('');
      return;
    }
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return value === '' ? '' : settled;
}

/** A list of suggestions as it is cached: with the question it is the answer to. */
interface Answered<Item> {
  /** What the search was made in: the country, and the region or the city. */
  within: string;
  text: string;
  items: Item[];
}

/**
 * The search behind a field that suggests places while one is typed.
 *
 * Unlike a search for a record, nothing typed is a question too: it lists the
 * largest cities of the country, or the most used areas of the city, so the
 * field has something to offer the moment it is opened. So it asks as soon as
 * it knows where to look (`within` is not empty), and again once typing has
 * paused.
 *
 * `data` is the suggestions and `answeredFor` the text they answer, trimmed,
 * and empty both for no data and for the answer to nothing typed: a screen
 * that has to say something about exactly what is in the field (that the
 * list has no such city, so "Use what I typed" is the offer) waits until
 * `data` is there and `answeredFor` is the field's own trimmed text. While a
 * longer text is being asked for, the answer to the shorter one stays; an
 * answer about another country or another city never does.
 */
function usePlaceSuggestions<Item>(
  within: string,
  text: string,
  keyOf: (settled: string) => QueryKey,
  ask: (settled: string) => Promise<Item[]>,
) {
  const settled = useSettled(text.trim(), PLACE_SEARCH_DEBOUNCE_MS);
  const enabled = within !== '';

  const query = useQuery({
    queryKey: keyOf(settled),
    queryFn: async (): Promise<Answered<Item>> => ({ within, text: settled, items: await ask(settled) }),
    enabled,
    staleTime: PLACES_STALE_MS,
    placeholderData: (previous) =>
      enabled && previous?.within === within ? previous : undefined,
  });

  return { ...query, data: query.data?.items, answeredFor: query.data?.text ?? '' };
}

/**
 * The cities of a country suggested while one is typed, the best match first:
 * found by any part of the name, with or without its accents. `regionCode`
 * narrows them to one region where the person has chosen one. Each carries
 * the region it is in, so choosing a city can set the region; one with a null
 * `id` is not in the list but is a city some doctor of the country has, typed
 * by hand. Nothing typed lists the largest places. Asks for nothing until
 * there is a country. See `usePlaceSuggestions` for `answeredFor`.
 */
export function useCitySearch(
  country: string | null | undefined,
  regionCode: string | null | undefined,
  text: string,
) {
  const repo = useGeoRepository();
  const code = (country ?? '').trim().toUpperCase();
  const region = (regionCode ?? '').trim();

  return usePlaceSuggestions(
    code === '' ? '' : `${code}/${region}`,
    text,
    (settled) => keys.geo.cities(code, region, settled),
    (settled) =>
      repo.cities({
        country: code,
        region: region === '' ? null : region,
        q: settled,
        limit: PLACE_SEARCH_LIMIT,
      }),
  );
}

/**
 * The areas of a city suggested while one is typed: the areas the doctors of
 * that city already have, the most used first. There is no list of areas
 * anywhere, so this is what was typed before, offered again. Asks for nothing
 * until there are both a country and a city.
 */
export function useAreaSearch(
  country: string | null | undefined,
  city: string | null | undefined,
  text: string,
) {
  const repo = useGeoRepository();
  const code = (country ?? '').trim().toUpperCase();
  const place = (city ?? '').trim();

  return usePlaceSuggestions(
    code === '' || place === '' ? '' : `${code}/${place}`,
    text,
    (settled) => keys.geo.areas(code, place, settled),
    (settled) =>
      repo.areas({ country: code, city: place, q: settled, limit: PLACE_SEARCH_LIMIT }),
  );
}

/**
 * The telephone library: every country's calling code, a national number
 * formatted while it is typed and checked by that country's rules, and a
 * number as E.164 and as people read one. `data` is undefined for the moment
 * the library takes to arrive, which is the first time any screen asks, and
 * then the same book for the life of the page; everything in it answers at
 * once. See `PhoneNumbers` for what it holds.
 */
export function usePhoneBook() {
  const book = usePhoneBookPort();
  return useQuery({
    queryKey: keys.phoneBook,
    queryFn: () => book.load(),
    staleTime: Infinity,
    gcTime: Infinity,
    // A book of functions, not data: there is nothing to compare with the last one.
    structuralSharing: false,
  });
}
